import React from 'react';
import PropTypes from 'prop-types';
import { HotTable } from '@handsontable/react';
import { map, partial, isEmpty } from 'lodash';
import { rgba2hex, isTextLight, UI_COLOR_DEFAULT, UI_COLOR_WHITE, ui, uiA } from 'envy-ui';
import 'handsontable/dist/handsontable.full.min.css';
import ReactResizeDetector from 'react-resize-detector';

export default function paTable({ height = 200, config }) {
  const currentConfig = JSON.parse(config);
  const {
    gridData: [headers, ...data],
    colWidths: manualColumnResize,
  } = currentConfig;
  const columns = map(headers, (_, index) => ({
    data: `${index}.displayValue`,
  }));
  const colHeaders = map(headers, 'displayValue');

  const tableWidth = (manualColumnResize ?? []).reduce((total, width) => total + width, 0);

  const calculateAutoColor = (backgroundColor) => {
    const hexColor = rgba2hex(backgroundColor) || backgroundColor;
    return isTextLight(hexColor) ? UI_COLOR_WHITE : UI_COLOR_DEFAULT;
  };

  const applyCellStyles = (tableCell, styles) => {
    const { isAutoColor = true, ...cssStyles } = styles;

    Object.entries(cssStyles).forEach(([property, styleValue]) => {
      tableCell.style[property] = styleValue;
    });

    if (isAutoColor && styles.backgroundColor) {
      tableCell.style.color = calculateAutoColor(styles.backgroundColor);
    }
  };

  const handleColHeaderStyling = (headers, col, tableHeader) => {
    const headerStyles = headers.map((e) => e.styles || {});

    const styles = headerStyles[col];

    if (styles) {
      applyCellStyles(tableHeader, styles);
    }
  };

  const handleCellStyling = (td, row, col, value, displayData) => {
    const { hasError, condition, color, styles } = displayData[row][col];

    if (styles) {
      if (isEmpty(styles)) {
        return;
      }

      applyCellStyles(td, styles);
      return;
    }

    if (!isEmpty(value)) {
      if (hasError && value !== 'Total') {
        td.style.backgroundColor = '#ff4c42';
        return;
      }

      if (condition) {
        td.style.backgroundColor = color;
        td.style.color = isTextLight(rgba2hex(color)) ? UI_COLOR_WHITE : UI_COLOR_DEFAULT;
      }
    }
  };

  const renderer = (instance, td, row, col, prop, value) => {
    const newTd = td;

    handleCellStyling(newTd, row, col, value, data);

    newTd.innerHTML = value;

    return newTd;
  };

  return (
    <ReactResizeDetector
      handleWidth
      handleHeight
      refreshMode="throttle"
      refreshRate={500}
      refreshOptions={{ trailing: true }}
      render={({ width }) => {
        // Adjust table width to avoid horizontal scrollbar
        const tableWidthAdjusted = tableWidth > 0 ? Math.min(tableWidth + 15, width) : width;
        return (
          <div {...ui(uiA`absolute w-100% h-100%`)} style={{ justifyItems: 'center' }}>
            <HotTable
              licenseKey="c0dbe-44d92-e53e5-94b2d-efa5b"
              height={height}
              columns={columns}
              data={data}
              allowInsertColumn={false}
              allowRemoveColumn={false}
              colHeaders={colHeaders}
              afterGetColHeader={partial(handleColHeaderStyling, headers)}
              className="pa-visual-table"
              manualColumnResize={manualColumnResize}
              colWidths={manualColumnResize}
              renderer={renderer}
              viewportColumnRenderingOffset={50}
              viewportRowRenderingOffset={100}
              width={tableWidthAdjusted}
              readOnly
            />
          </div>
        );
      }}
    />
  );
}

paTable.propTypes = {
  config: PropTypes.string.isRequired,
  height: PropTypes.number,
};
